import { useRef, type ReactNode } from 'react';
import type { ImageAsset } from '@/assets';
import { cssVars } from '@/lib/cssVars';
import type { Orientation } from '@/lib/orientation';
import { SceneCanvas } from '@/pixi/SceneCanvas';
import styles from './GameStage.module.css';

interface GameStageProps {
  /** Background painting per layout orientation; CSS swaps them at the same breakpoint. */
  backgrounds: Record<Orientation, ImageAsset>;
  /** PixiJS effects drawn over the background, e.g. lights placed in a `CoverSpace`. */
  scene?: ReactNode;
  /** CSS background-position (default centre), e.g. to keep a painted landmark at one edge. */
  backgroundPosition?: string;
  /**
   * Depth of field on something in front (Alex's hologram): the painting cross-fades to
   * `focusBackgrounds` (the same painting through a lens focused on the floor in front, with
   * depth-based blur and bokeh: ml/art/bokeh.py), or without them goes soft and a little darker.
   */
  focus?: boolean;
  focusBackgrounds?: Record<Orientation, ImageAsset> | undefined;
  children: ReactNode;
}

/**
 * The frame every screen renders into: a full-viewport painted background (CSS, so it shows
 * before PixiJS starts), an effects canvas over it, and the screen's UI in a centred column that
 * scales from phones to tablets in either orientation.
 */
export function GameStage({
  backgrounds,
  scene,
  backgroundPosition,
  focus = false,
  focusBackgrounds,
  children,
}: GameStageProps) {
  const stageRef = useRef<HTMLDivElement>(null);

  return (
    <div
      ref={stageRef}
      className={[styles.stage, focus && styles.focus, focus && focusBackgrounds && styles.lens]
        .filter(Boolean)
        .join(' ')}
      style={cssVars({
        '--stage-image-portrait': `url(${backgrounds.portrait.url})`,
        '--stage-image-landscape': `url(${backgrounds.landscape.url})`,
        '--stage-position': backgroundPosition ?? 'center',
        ...(focusBackgrounds
          ? {
              '--focus-image-portrait': `url(${focusBackgrounds.portrait.url})`,
              '--focus-image-landscape': `url(${focusBackgrounds.landscape.url})`,
            }
          : {}),
      })}
    >
      <div className={styles.backdrop} aria-hidden />
      {focusBackgrounds && <div className={styles.lensBackdrop} aria-hidden />}
      {scene && (
        <div className={styles.scene} aria-hidden>
          <SceneCanvas resizeTo={stageRef}>{scene}</SceneCanvas>
        </div>
      )}
      <div className={styles.ui}>{children}</div>
    </div>
  );
}
